import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Tab,
  Tabs,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AppHeader from '../components/AppHeader';
import AlertModal from '../components/AlertModal';
import { useOrders, ORDER_STATUS_CATEGORIES } from '../contexts/OrderContext';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import { updateOrderStatus } from '../services/orderService';

const getStatusColor = (status) => {
  const normalizedStatus = status?.trim().toLowerCase();
  switch (normalizedStatus) {
    case 'confirmed': return '#10B981';
    case 'preparing': return '#F59E0B';
    case 'ready to pickup': return '#851E41';
    case 'out for delivery': return '#8B5CF6';
    case 'rider assigned': return '#06B6D4';
    case 'rider reached pickup': return '#F59E0B';
    case 'rider picked up': return '#10B981';
    default: return '#6B7280';
  }
};

const OrderCard = ({ order, onStatusChange, onViewDetails, listenAllMode, allBranches }) => {
  const statusColor = getStatusColor(order.status);

  const getBranchName = () => {
    if (!listenAllMode || !allBranches || allBranches.length === 0) return null;
    const branchId = order.branchId || order.branch?._id || (typeof order.branch === 'string' ? order.branch : null);
    if (!branchId) return null;
    const branch = allBranches.find(b => b._id === branchId);
    return branch?.name || null;
  };

  const branchName = getBranchName();

  const handleStart = async (e) => {
    e.stopPropagation();
    await onStatusChange(order._id, 'Preparing');
  };

  const handleReady = async (e) => {
    e.stopPropagation();
    await onStatusChange(order._id, 'Ready to Pickup');
  };

  return (
    <Card
      sx={{
        mb: 1.5,
        borderRadius: 3,
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
      }}
      onClick={() => onViewDetails(order)}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
            {order.orderId || order._id}
          </Typography>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: statusColor,
            }}
          />
        </Box>

        <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#6B7280', mb: 1, textTransform: 'capitalize' }}>
          {order.status}
        </Typography>

        {branchName && (
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#10B981', mb: 0.5 }}>
            {branchName}
          </Typography>
        )}

        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#374151', mb: 0.5 }}>
          {order.customer?.name || 'Customer'}
        </Typography>

        {order.items && order.items.length > 0 && (
          <Typography sx={{ fontSize: 12, color: '#6B7280', mb: 1 }}>
            {order.items.length} item{order.items.length > 1 ? 's' : ''}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, pt: 1.5, borderTop: '1px solid #F3F4F6' }}>
          {order.status === 'Confirmed' && (
            <Button
              size="small"
              variant="contained"
              startIcon={<RestaurantIcon sx={{ fontSize: 14 }} />}
              onClick={handleStart}
              sx={{
                bgcolor: '#FEF3C7',
                color: '#B45309',
                fontSize: 12,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { bgcolor: '#FDE68A' },
              }}
            >
              Start
            </Button>
          )}
          {order.status === 'Preparing' && (
            <Button
              size="small"
              variant="contained"
              startIcon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
              onClick={handleReady}
              sx={{
                bgcolor: '#D1FAE5',
                color: '#047857',
                fontSize: 12,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { bgcolor: '#A7F3D0' },
              }}
            >
              Ready
            </Button>
          )}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#851E41', mr: 0.5 }}>
              View
            </Typography>
            <ChevronRightIcon sx={{ fontSize: 16, color: '#851E41' }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const OrdersPage = () => {
  const navigate = useNavigate();
  const { orders, loading, fetchOrders, getOrdersByStatus, getOrderCounts } = useOrders();
  const { isAuthenticated } = useAuth();
  const { selectedBranch, listenAllMode, allBranches } = useBranch();
  const [activeTab, setActiveTab] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');

  const showAlert = (title, message) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertVisible(true);
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated]);

  const orderCounts = getOrderCounts();

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      if (isAuthenticated) {
        await fetchOrders();
      }
    } catch (error) {
      
    } finally {
      setRefreshing(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      await fetchOrders();
    } catch (error) {
      
      showAlert('Error', 'Failed to update order status. Please try again.');
    }
  };

  const handleViewDetails = (order) => {
    navigate('/order-details', { state: { order } });
  };

  const tabs = [
    { key: ORDER_STATUS_CATEGORIES.CONFIRMED, label: 'Confirmed', count: orderCounts.confirmed },
    { key: ORDER_STATUS_CATEGORIES.PREPARING, label: 'Preparing', count: orderCounts.preparing },
    { key: ORDER_STATUS_CATEGORIES.READY_TO_PICKUP, label: 'Ready', count: orderCounts.readyToPickup },
  ];

  const filteredOrders = getOrdersByStatus(tabs[activeTab].key);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      <AppHeader title="Orders" />
      <AlertModal
        visible={alertVisible}
        title={alertTitle}
        message={alertMessage}
        onRequestClose={() => setAlertVisible(false)}
      />

      <Box sx={{ borderBottom: 1, borderColor: '#E5E7EB', bgcolor: '#fff', px: 1.5, pt: 1.5 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minHeight: 44,
              textTransform: 'none',
              fontSize: 13,
              fontWeight: 600,
              color: '#6B7280',
              bgcolor: '#F3F4F6',
              borderRadius: 2,
              mx: 0.5,
              px: 1.5,
              '&.Mui-selected': {
                bgcolor: '#851E41',
                color: '#fff',
              },
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.key}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {tab.label}
                  <Box
                    sx={{
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: activeTab === index ? 'rgba(255,255,255,0.3)' : '#E5E7EB',
                      minWidth: 20,
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: activeTab === index ? '#fff' : '#6B7280',
                      }}
                    >
                      {tab.count}
                    </Typography>
                  </Box>
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ p: 1.5 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#851E41' }} />
          </Box>
        ) : filteredOrders.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                bgcolor: '#F1F5F9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <ReceiptIcon sx={{ fontSize: 48, color: '#E5E7EB' }} />
            </Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1E293B', mb: 1 }}>
              No {tabs[activeTab].label.toLowerCase()} orders
            </Typography>
          </Box>
        ) : (
          filteredOrders.map((order) => (
            <OrderCard
              key={order._id}
              order={order}
              onStatusChange={handleStatusChange}
              onViewDetails={handleViewDetails}
              listenAllMode={listenAllMode}
              allBranches={allBranches}
            />
          ))
        )}
      </Box>

      {refreshing && (
        <Box
          sx={{
            position: 'fixed',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: '#1E293B',
            color: '#fff',
            px: 2,
            py: 1,
            borderRadius: 2,
            zIndex: 1000,
          }}
        >
          <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
          Refreshing...
        </Box>
      )}
    </Box>
  );
};

export default OrdersPage;